const { protocol, hostname } = window.location;
const rootDomain = hostname.split('.').slice(-2).join('.') || hostname;

/**
 * Dev mode is detected via Vite's compile-time flag rather than the hostname.
 * This way the proxy is used for any dev hostname — `localhost`, `127.0.0.1`,
 * or a hosts-file alias like `app-dev.sgpdev.com` — so we always avoid CORS
 * issues when calling the backend from Vite.
 *
 * In production builds (`npm run build`), import.meta.env.DEV is `false` and
 * calls go directly to `https://{server}.sgpdev.com`.
 */
const isDevServer = import.meta.env.DEV;

const effectiveDomain = rootDomain;
const effectiveProtocol = protocol;

/** Allowed API server name prefixes. */
const ALLOWED_SERVERS = new Set([
  'spiglassapi',
  'spiglassadminapi',
  'spiglassgoapi',
  'corescriptapi',
  'vmapi',
  'tokenadmin',
  'mindstudioapi',
  'componentsapi',
  'safeview',
  'safeviewapi',
  'pinpoint',
  'rolemasterv1',
  'mediav2',
  'vendorconnect',
  'image',
  'disputeagent',
]);

/**
 * Build the base URL for an API server.
 *
 * On the Vite dev server: Returns `/devapi/{server}` — the proxy forwards to
 *                         the real API and the browser's existing Bearer
 *                         cookie (set by `authentication.sgpdev.com` after
 *                         Windows Auth) is forwarded through.
 * On deployed:            Returns `https://{server}.sgpdev.com` — direct
 *                         cross-subdomain call with `withCredentials: true`.
 *
 * Pass `{ useDevProxy: false }` for services that must be called directly even
 * from Vite dev, e.g. `safeviewapi.{root-domain}`.
 */
export function buildApiBase(server = 'spiglassapi', options: { useDevProxy?: boolean } = {}) {
  if (!ALLOWED_SERVERS.has(server)) {
    throw new Error(`[buildApiBase] Unknown API server: "${server}"`);
  }
  if (isDevServer && options.useDevProxy !== false) {
    return `/devapi/${server}`;
  }

  return `${effectiveProtocol}//${server}.${effectiveDomain}`;
}
